import { type ComponentPropsWithRef, type ComponentType, type ReactNode, type SVGProps } from 'react';

import { cn } from '@/lib/utils';
import { Button, Icon, Typography } from '@/shared-ui/atoms';
import { Select } from '@/shared-ui/atoms/select';
import { HeartNoFillIcon, InfoIcon } from '@/shared-ui/icons';
import { Price as PriceDisplay } from '@/shared-ui/molecules/price';

type RootProps = ComponentPropsWithRef<'section'>;

const Root = ({ className, ref, ...props }: RootProps) => (
    <section
        data-slot="sticky-button"
        ref={ref}
        className={cn(
            'sticky bottom-0 z-30 flex w-full flex-col gap-2 p-4 shadow-[0_-2px_6px_0_rgba(0,0,0,0.15)]',
            'lg:flex-row lg:items-center lg:gap-6',
            className
        )}
        {...props}
    />
);

Root.displayName = 'StickyButton.Root';

type ProductProps = ComponentPropsWithRef<'div'> & {
    brand: ReactNode;
    name: ReactNode;
};

const Product = ({ brand, className, name, ref, ...props }: ProductProps) => (
    <div
        data-slot="sticky-button-product"
        ref={ref}
        className={cn('hidden min-w-0 flex-1 lg:block', className)}
        {...props}>
        <Typography className={'font-normal'} variant={'h2'}>
            {brand}
        </Typography>
        <p className="text-base tracking-[0.01rem]">{name}</p>
    </div>
);

Product.displayName = 'StickyButton.Product';

type ActionsProps = ComponentPropsWithRef<'div'>;

const Actions = ({ className, ref, ...props }: ActionsProps) => (
    <div
        data-slot="sticky-button-actions"
        ref={ref}
        className={cn('flex flex-col gap-3 sm:flex-row sm:items-center md:flex-[0_0_auto]', className)}
        {...props}
    />
);

Actions.displayName = 'StickyButton.Actions';

type WishlistIconProps = Omit<ComponentPropsWithRef<'button'>, 'children'> & {
    label?: string;
};

const WishlistIcon = ({ className, label = 'Add to wishlist', ref, type = 'button', ...props }: WishlistIconProps) => (
    <button
        aria-label={label}
        data-slot="sticky-button-wishlist-icon"
        ref={ref}
        type={type}
        className={cn(
            'text-foreground hover:text-primary focus-visible:border-ring focus-visible:ring-ring/50 hidden size-10 shrink-0 items-center justify-center bg-transparent outline-none transition-colors focus-visible:ring-[3px] lg:flex',
            className
        )}
        {...props}>
        <Icon aria-hidden="true" className="size-6" icon={HeartNoFillIcon} />
    </button>
);

WishlistIcon.displayName = 'StickyButton.WishlistIcon';

export type StickyButtonVariantOption = {
    value: string;
    label: string;
    disabled?: boolean;
};

type VariantSelectProps = {
    ariaLabel?: string;
    className?: string;
    contentClassName?: string;
    defaultValue?: string;
    disabled?: boolean;
    name?: string;
    onValueChange?: (value: string) => void;
    options: StickyButtonVariantOption[];
    placeholder?: string;
    value?: string;
    width?: number | string;
};

const VariantSelect = ({
    ariaLabel = 'Select variant',
    className,
    contentClassName,
    defaultValue,
    disabled,
    name,
    onValueChange,
    options,
    placeholder = 'Select',
    value,
    width = 318,
}: VariantSelectProps) => (
    <Select.Root
        className={cn('hidden lg:block', className)}
        defaultValue={defaultValue}
        disabled={disabled}
        name={name}
        onValueChange={onValueChange}
        placeholder={placeholder}
        value={value}
        width={width}>
        <Select.Trigger aria-label={ariaLabel} className="h-12">
            <Select.TriggerText />
            <Select.TriggerIcon />
        </Select.Trigger>
        <Select.Content className={contentClassName}>
            {options.map((option) => (
                <Select.Option key={option.value} disabled={option.disabled} value={option.value}>
                    {option.label}
                </Select.Option>
            ))}
        </Select.Content>
    </Select.Root>
);

VariantSelect.displayName = 'StickyButton.VariantSelect';

type MessageProps = ComponentPropsWithRef<'div'> & {
    icon?: ComponentType<SVGProps<SVGSVGElement>>;
};

const Message = ({ children, className, icon = InfoIcon, ref, ...props }: MessageProps) => {
    return (
        <div
            data-slot="sticky-button-message"
            ref={ref}
            className={cn('md:hidden flex items-center justify-center gap-2', className)}
            {...props}>
            <Icon aria-hidden="true" className="size-6" icon={icon} />
            <Typography variant="p">{children}</Typography>
        </div>
    );
};

Message.displayName = 'StickyButton.Message';

type PriceProps = Omit<ComponentPropsWithRef<'div'>, 'children'> & {
    label?: ReactNode;
    children: ReactNode;
};

const Price = ({ children, className, label = 'Price:', ref, ...props }: PriceProps) => (
    <div
        data-slot="sticky-button-price"
        ref={ref}
        className={cn('flex items-center justify-between uppercase tracking-[0.09rem] md:hidden', className)}
        {...props}>
        <Typography as="span" variant="p">
            {label}
        </Typography>
        <PriceDisplay.Root className="items-end">
            <PriceDisplay.Current>{children}</PriceDisplay.Current>
        </PriceDisplay.Root>
    </div>
);

Price.displayName = 'StickyButton.Price';

type CTAProps = Omit<ComponentPropsWithRef<typeof Button>, 'variant' | 'price' | 'originalPrice' | 'children'> & {
    children?: ReactNode;
    label?: ReactNode;
    originalPrice?: string;
    price?: string;
    variant?: 'purchase' | 'secondary';
};

const CTA = ({
    children,
    variant = 'purchase',
    className,
    label = 'Add to Bag',
    originalPrice,
    price,
    ...props
}: CTAProps) =>
    price ? (
        <Button
            data-slot="sticky-button-add-to-bag"
            variant="purchase"
            className={cn('h-12 w-full justify-between gap-4 px-4 lg:w-auto', className)}
            {...props}>
            <span className="flex-1 text-center">{children ?? label}</span>
            <PriceDisplay.Root className="shrink-0 items-end">
                {originalPrice ? (
                    <PriceDisplay.Group>
                        <PriceDisplay.Original className="text-inherit opacity-60">
                            {originalPrice}
                        </PriceDisplay.Original>
                        <PriceDisplay.Current className="text-inherit">{price}</PriceDisplay.Current>
                    </PriceDisplay.Group>
                ) : (
                    <PriceDisplay.Current className="text-inherit">{price}</PriceDisplay.Current>
                )}
            </PriceDisplay.Root>
        </Button>
    ) : (
        <Button
            data-slot="sticky-button-add-to-bag"
            variant={variant}
            className={cn('h-12 px-4 w-full lg:w-auto', className)}
            {...props}>
            {children ?? label}
        </Button>
    );

CTA.displayName = 'StickyButton.CTA';

const StickyButton = {
    Root,
    Product,
    Actions,
    WishlistIcon,
    VariantSelect,
    Message,
    Price,
    CTA,
} as const;

export { StickyButton };
